import { Injectable } from '@angular/core';
import { DictionaryService } from '@app/core/services/dictionary.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { UserService } from '@app/core/services/user.service';
import { Corridor, DossierSearchCriteria, DossierSearchType, InvolvedAgencyTrafficLight } from '@app/pcs/models';
import { IM_PERMANENT_ACCEPTANCE } from '@app/pcs/reducer/advanced-search/search-form';
import { hasValue, safeProp } from '@util';
import * as R from 'ramda';

type PrepareSearchCriteria = (searchCriteriaFormat: DossierSearchCriteria) => DossierSearchCriteria;
const prepareSearchCriteria = <PrepareSearchCriteria>R.omit(['searchType', 'agencyType', 'trainSpeed', 'networkPaPOnly']);

const sameAgencyType = R.curry((agencyTypeId) => R.propEq('agencyType', agencyTypeId));
const sameSearchType = R.curry((searchType) => R.propEq('searchType', searchType));

type SameAgencyAndSearchType = (agencyTypeId: string, searchType: DossierSearchType) => (searchCriteria: DossierSearchCriteria) => boolean;
const sameAgencyAndSearchType: SameAgencyAndSearchType = (agencyTypeId: string, searchType: DossierSearchType) => R.allPass([sameAgencyType(agencyTypeId), sameSearchType(searchType)]);

const prepareSearchCriteriaToSave = (searchCriteria: DossierSearchCriteria, searchType: DossierSearchType, agencyType: string): DossierSearchCriteria =>
  R.mergeDeepRight(searchCriteria, {
    searchType,
    agencyType
  });

type InvolvedAgencyTrafficLightsWithoutPermanentAcceptance = (involvedAgencyTrafficLightPairs: InvolvedAgencyTrafficLight[]) => boolean;
const involvedAgencyTrafficLightsWithoutPermanentAcceptance: InvolvedAgencyTrafficLightsWithoutPermanentAcceptance =
  R.any(R.compose(R.not, R.has(IM_PERMANENT_ACCEPTANCE)));

@Injectable()
export class SearchCriteriaService {

  constructor(private userService: UserService,
    private localStorageService: LocalStorageService,
    private dictionaryService: DictionaryService) {
  }

  getSearchCriteria(searchType: DossierSearchType): DossierSearchCriteria {
    const searchCriteria = this.migrateSearchCriteria(this.localStorageService.read<DossierSearchCriteria>('searchCriteria'));
    return hasValue(searchCriteria) && sameAgencyAndSearchType(this.getAgencyTypeId(), searchType)(searchCriteria) ? prepareSearchCriteria(searchCriteria) : null;
  }

  setSearchCriteria(searchCriteria: DossierSearchCriteria, searchType: DossierSearchType): void {
    this.localStorageService.write('searchCriteria', prepareSearchCriteriaToSave(searchCriteria, searchType, this.getAgencyTypeId()));
  }

  removeSearchCriteria() {
    this.localStorageService.remove('searchCriteria');
  }

  private getAgencyTypeId() {
    return safeProp('id', this.userService.getAgencyTypeId());
  }

  private migrateSearchCriteria(searchCriteria: any) {
    let updatedSearchCriteria = searchCriteria;
    const shouldMigrateCorridors = hasValue(searchCriteria) && hasValue(searchCriteria.rfcCorridors)
      && searchCriteria.rfcCorridors[0].agencyId;
    const shouldMigrateCorridor = searchCriteria?.rfcCorridor?.agencyId;
    const shouldMigrateInvolvedAgenciesTrafficLightPairs = hasValue(searchCriteria) && hasValue(searchCriteria.involvedAgencyTrafficLightPairs)
    && involvedAgencyTrafficLightsWithoutPermanentAcceptance(searchCriteria.involvedAgencyTrafficLightPairs);

    if (shouldMigrateCorridors) {
      updatedSearchCriteria = R.mergeRight(updatedSearchCriteria, {rfcCorridors: this.getCorridors(updatedSearchCriteria.rfcCorridors)});
    }

    if (shouldMigrateCorridor) {
      updatedSearchCriteria = R.mergeRight(updatedSearchCriteria, {rfcCorridor: R.head(this.getCorridor(updatedSearchCriteria.rfcCorridor))});
    }

    if(shouldMigrateInvolvedAgenciesTrafficLightPairs) {
      const involvedAgencyTrafficLightPairs = updatedSearchCriteria.involvedAgencyTrafficLightPairs;
      updatedSearchCriteria = R.mergeRight(updatedSearchCriteria, {
        involvedAgencyTrafficLightPairs: this.defineInvolvedAgenciesPermanentAcceptance(involvedAgencyTrafficLightPairs)
      });
    }

    return updatedSearchCriteria;
  }

  private defineInvolvedAgenciesPermanentAcceptance = (involvedAgencyTrafficLightPairs: InvolvedAgencyTrafficLight[]) =>
    R.map(light => R.mergeRight(light, {
      imPermanentAcceptance: null
    }), involvedAgencyTrafficLightPairs);

  private getCorridor(corridor: Corridor) {
    return R.filter((c: Corridor) => c.id === corridor.id, this.dictionaryService.getCorridors());
  }

  private getCorridors(corridors: Corridor[]) {
    return R.flatten(R.map((corridor: Corridor) => this.getCorridor(corridor), corridors));
  }
}
